"use client";

import { useEffect } from "react";
import Script from "next/script";
import { siteConfig } from "@/lib/site.config";

const STORAGE_KEY = siteConfig.cookieStorageKey;
const CONSENT_EVENT = "cookie-consent-changed";

type ConsentState = "granted" | "denied";

interface ConsentUpdate {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
}

const GRANTED: ConsentUpdate = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

const DENIED: ConsentUpdate = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Analytics must only run on the public production site. Vercel deployments
// (*.vercel.app) and CMS draft-preview routes (/preview…) render this same
// layout, so the GTM bootstrap below is gated by hostname and path — otherwise
// those environments send hits and show up in GA4 / Ahrefs as tracked pages.
const SITE_HOST = new URL(siteConfig.url).hostname.replace(/^www\./, "");

const TRACKING_GUARD =
  `var h=w.location.hostname,p=w.location.pathname;` +
  `if((h!=='${SITE_HOST}'&&h!=='www.${SITE_HOST}')` +
  `||h.slice(-11)==='.vercel.app'` +
  `||p==='/preview'||p.indexOf('/preview/')===0)return;`;

interface GoogleTagManagerProps {
  gtmId?: string | null;
}

export function GoogleTagManager({ gtmId }: GoogleTagManagerProps) {
  useEffect(() => {
    if (!gtmId) return;

    const updateConsent = (state: ConsentUpdate) => {
      if (typeof window.gtag === "function") {
        window.gtag("consent", "update", state);
      }
    };

    const applyStoredConsent = () => {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "accepted") updateConsent(GRANTED);
      else if (stored === "rejected") updateConsent(DENIED);
    };

    applyStoredConsent();

    const onConsentChanged = () => applyStoredConsent();
    window.addEventListener(CONSENT_EVENT, onConsentChanged as EventListener);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onConsentChanged as EventListener);
    };
  }, [gtmId]);

  if (!gtmId) return null;

  return (
    <>
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html:
            `(function(w,d,s,l,i){${TRACKING_GUARD}w[l]=w[l]||[];w[l].push({'gtm.start':` +
            `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
            `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
            `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
            `})(window,document,'script','dataLayer','${gtmId}');`,
        }}
      />
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
    </>
  );
}

export const GTM_CONSENT_DEFAULT_SCRIPT =
  `window.dataLayer=window.dataLayer||[];` +
  `window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};` +
  `window.gtag('consent','default',{` +
    `'ad_storage':'denied',` +
    `'ad_user_data':'denied',` +
    `'ad_personalization':'denied',` +
    `'analytics_storage':'denied',` +
    `'wait_for_update':500` +
  `});`;
